import * as XLSX from "xlsx";
import { cleanText, normalizePhone, normalizeSearch } from "../../../shared/utils/format";
import { duplicateKey, parseLocationText } from "../../shops/utils";
import type { ImportRowIssue, Shop, ShopClass, ShopCreateInput } from "../../shops/types";

export type ImportField =
  | "shopName"
  | "shopPhoneNumber"
  | "shopClass"
  | "shopGovenorate"
  | "shopCity"
  | "shopDistrict"
  | "shopStreet"
  | "shopLocation";

export type ParsedSheet = {
  headers: string[];
  rows: unknown[][];
};

export type ImportColumnMapping = Partial<Record<ImportField, number | "">>;

export type ImportPreviewRow = {
  rowNumber: number;
  shopName: string | null;
  shop: ShopCreateInput | null;
  issues: ImportRowIssue[];
};

export type ImportPreview = {
  rows: ImportPreviewRow[];
  validShops: ShopCreateInput[];
  rejectedRows: number;
  warningCount: number;
};

export const IMPORT_FIELDS: Array<{ field: ImportField; label: string; required?: boolean }> = [
  { field: "shopName", label: "Shop name", required: true },
  { field: "shopPhoneNumber", label: "Phone number" },
  { field: "shopClass", label: "Class" },
  { field: "shopGovenorate", label: "Governorate" },
  { field: "shopCity", label: "City" },
  { field: "shopDistrict", label: "District" },
  { field: "shopStreet", label: "Street" },
  { field: "shopLocation", label: "Location / coordinates" }
];

const headerAliases: Record<ImportField, string[]> = {
  shopName: ["shopname", "shop name", "name", "store", "store name", "customer", "customer name"],
  shopPhoneNumber: ["shopphonenumber", "shop phone number", "phone", "mobile", "number", "telephone"],
  shopClass: ["shopclass", "shop class", "class", "grade"],
  shopGovenorate: [
    "shopgovenorate",
    "shopgovernorate",
    "governorate",
    "governorate name",
    "province",
    "محافظة"
  ],
  shopCity: ["shopcity", "shop city", "city", "area", "مدينة"],
  shopDistrict: ["shopdistrict", "shop district", "district", "region", "neighborhood", "منطقة"],
  shopStreet: ["shopstreet", "shop street", "street", "road", "شارع"],
  shopLocation: ["shoplocation", "shop location", "location", "coordinates", "lat lng", "gps", "map"]
};

function rowValue(row: unknown[], index: number | "" | undefined): string | null {
  if (typeof index !== "number") {
    return null;
  }

  return cleanText(row[index]);
}

function parseShopClass(value: string | null, issues: ImportRowIssue[], rowNumber: number): ShopClass | null {
  if (!value) {
    return null;
  }

  const normalized = value.trim().toUpperCase();

  if (normalized === "A" || normalized === "B" || normalized === "C") {
    return normalized;
  }

  issues.push({
    rowNumber,
    field: "shopClass",
    message: `Class "${value}" was ignored. Use A, B, or C.`,
    severity: "warning"
  });

  return null;
}

export async function parseSpreadsheetFile(file: File): Promise<ParsedSheet> {
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data, { type: "array" });
  const sheetName = workbook.SheetNames[0];

  if (!sheetName) {
    throw new Error("The file does not contain a readable sheet.");
  }

  const sheet = workbook.Sheets[sheetName];
  const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: ""
  });

  if (matrix.length === 0) {
    throw new Error("The first sheet is empty.");
  }

  const [headerRow, ...rows] = matrix;
  const headers = headerRow.map((value, index) => cleanText(value) ?? `Column ${index + 1}`);

  return {
    headers,
    rows: rows.filter((row) => row.some((cell) => cleanText(cell)))
  };
}

export function autoMapHeaders(headers: string[]): ImportColumnMapping {
  const mapping: ImportColumnMapping = {};
  const normalizedHeaders = headers.map((header) => normalizeSearch(header).replace(/[_-]/g, " "));

  for (const option of IMPORT_FIELDS) {
    const aliases = headerAliases[option.field].map((alias) => normalizeSearch(alias).replace(/[_-]/g, " "));
    const index = normalizedHeaders.findIndex((header) => aliases.includes(header));

    if (index >= 0) {
      mapping[option.field] = index;
    }
  }

  return mapping;
}

export function buildImportPreview(
  parsedSheet: ParsedSheet,
  mapping: ImportColumnMapping,
  existingShops: Shop[]
): ImportPreview {
  const existingKeys = new Set(existingShops.map(duplicateKey));
  const incomingKeys = new Set<string>();

  const rows = parsedSheet.rows.map((row, rowIndex) => {
    const rowNumber = rowIndex + 2;
    const issues: ImportRowIssue[] = [];
    const shopName = rowValue(row, mapping.shopName);

    if (!shopName) {
      issues.push({
        rowNumber,
        field: "shopName",
        message: "Shop name is required for import.",
        severity: "error"
      });

      return {
        rowNumber,
        shopName,
        shop: null,
        issues
      };
    }

    const shopPhoneNumber = normalizePhone(rowValue(row, mapping.shopPhoneNumber));
    const shopClass = parseShopClass(rowValue(row, mapping.shopClass), issues, rowNumber);
    const shopLocation = parseLocationText(rowValue(row, mapping.shopLocation));

    const shop: ShopCreateInput = {
      shopName,
      shopPhoneNumber,
      shopClass,
      shopGovenorate: rowValue(row, mapping.shopGovenorate),
      shopCity: rowValue(row, mapping.shopCity),
      shopDistrict: rowValue(row, mapping.shopDistrict),
      shopStreet: rowValue(row, mapping.shopStreet),
      shopLocation,
      source: "import",
      importBatchId: null
    };

    const key = duplicateKey({
      shopName: shop.shopName,
      shopPhoneNumber: shop.shopPhoneNumber
    });

    if (incomingKeys.has(key)) {
      issues.push({
        rowNumber,
        field: "shopName",
        message: "Possible duplicate inside this file.",
        severity: "warning"
      });
    }

    if (existingKeys.has(key)) {
      issues.push({
        rowNumber,
        field: "shopName",
        message: "Possible duplicate of an existing saved shop.",
        severity: "warning"
      });
    }

    incomingKeys.add(key);

    return {
      rowNumber,
      shopName,
      shop,
      issues
    };
  });

  return {
    rows,
    validShops: rows.flatMap((row) => (row.shop ? [row.shop] : [])),
    rejectedRows: rows.filter((row) => row.issues.some((issue) => issue.severity === "error")).length,
    warningCount: rows.flatMap((row) => row.issues).filter((issue) => issue.severity === "warning").length
  };
}
